/**
 * @module  public/js/services/sequenceEnrollmentService.js
 * @class   SequenceEnrollmentService
 * @summary Enrol contacts into a playbook and seed step-0 CRM task with Topsail metadata.
 *
 * @description
 * Reads step 0 from the Playbooks collection, computes a business-day due date, and creates the first
 * CRM task with a hidden HTML‑comment that stores step metadata in the task Notes field. It persists a
 * SequenceEnrolments document so the SequenceEngine worker can advance the playbook. Bulk enrolment is
 * supported via `enrolContacts`, which iterates seller/contact tuples with idempotent checks.
 *
 * ## Key Features
 * – Bulk‑enrol many (userId, contactId) pairs in one call.
 * – Idempotent: skips when an enrolment already exists for the tuple.
 * – Embeds step‑metadata JSON in `hs_task_body` / `Description` for later UI rendering.
 * – Calculates due dates using business‑day offsets and optional start date.
 *
 * ## Public API
 * – `enrolContacts(playbookId, tuples, startISO?)` — bulk enrol entry‑point.
 *
 * ## Dependencies
 * { addBusinessDayTimestamp, db, firebase/firestore, makeMetaComment }
 *
 * @author   TopSail AI
 * @version  1.6.0
 * @since    2025-05-11
 */

'use strict';

import {
  getDoc,
  query,
  where,
  getDocs,
  limit,
  serverTimestamp,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import {
  docWithTenant,
  colWithTenant,
  colGroupWithTenant,
  writeWithTenant,
} from '../utils/firestoreHelpers.js';
import { addBusinessDayTimestamp } from '../utils/date-utils.js';
import { makeMetaComment, normaliseCallScripts } from '../utils/taskMeta.js';
import {
  normalizeContactKey,
  buildContactCandidates,
} from '../utils/contactKey.js';

/* eslint-disable class-methods-use-this, no-await-in-loop */
export default class SequenceEnrollmentService {
  /** @type {import('../core/apiclient.js').default} */ #apiClient;
  /** @type {import('./authservice.js').default} */ #authService;
  /** @type {import('../core/endpoints-service.js').default} */ #endpoints;
  /**
   * @type {{ getCurrentCRM(uid:string):Promise<'hubspot'|'salesforce'|'local'|null> }}
   */
  #crmUtils;

  /**
   * Create a sequence enrollment service with explicit app dependencies.
   *
   * @param {object} dependencies - Runtime dependencies.
   * @param {import('../core/apiclient.js').default} dependencies.apiClient - Authenticated API client.
   * @param {import('./authservice.js').default} dependencies.authService - Current-user auth service.
   * @param {import('../core/endpoints-service.js').default} dependencies.endpoints - Endpoint registry.
   * @param {{ getCurrentCRM(uid:string):Promise<'hubspot'|'salesforce'|'local'|null> }} dependencies.crmUtils - CRM resolver helpers.
   */
  constructor({ apiClient, authService, endpoints, crmUtils }) {
    this.#apiClient = apiClient;
    this.#authService = authService;
    this.#endpoints = endpoints;
    this.#crmUtils = crmUtils;
  }

  /* ────────────────────────── PUBLIC ────────────────────────── */

  /**
   * Bulk‑enrol many (seller, contact) tuples in one playbook.
   * @param {string} playbookId – Firestore ID of the playbook
   * @param {{userId:string,contactId:string}[]} tuples – list of enrolments
   * @param {string|null} [startISO=null] – "YYYY-MM-DD" → 00:00 local or null = now()
   * @param {boolean} [skipCrmCreates] – opt-out of CRM creates (QA only)
   * @returns {Promise<{successIds:string[],skipIds:string[],failIds:string[]}>}
   */
  async enrolContacts(playbookId, tuples, startISO = null, skipCrmCreates) {
    const successIds = [];
    const skipIds = [];
    const failIds = [];

    for (const { userId, contactId } of tuples) {
      try {
        const res = await this.#enrolContact(
          playbookId,
          userId,
          contactId,
          startISO,
          skipCrmCreates
        );
        (res === 'ok' ? successIds : skipIds).push(contactId);
      } catch (err) {
        if (
          err?.code === 'needs_manual_reset' ||
          err?.code === 'contact_active_or_staged'
        ) {
          console.info(
            '[SequenceEnrollmentService] skipped contact',
            contactId,
            err.code
          );
          skipIds.push(contactId);
          continue;
        }
        console.error('[SequenceEnrollmentService] enrol fail →', err.message);
        failIds.push(contactId);
      }
    }
    return { successIds, skipIds, failIds };
  }

  /* ───────────────────────── helpers ───────────────────────── */

  /**
   * Enrol a single contact for a single seller.
   * @private
   * @param {string} playbookId
   * @param {string} userId
   * @param {string} contactId
   * @param {string|null} startISO
   * @param {boolean} [skipCrmCreates]
   * @returns {Promise<'ok'|'skip'>}
   */
  async #enrolContact(playbookId, userId, contactId, startISO, skipCrmCreates) {
    if (!playbookId || !userId || !contactId) {
      throw new Error('playbookId, userId & contactId are required');
    }

    const tenantId = await this.#resolveTenantId();
    const contactIdRaw = String(contactId).trim();
    const contactKey = normalizeContactKey(contactIdRaw);
    if (!contactKey) throw new Error('contactId required');

    const { shouldConsumeEligibility } = await this.#assertEnrollmentAllowed({
      tenantId,
      contactIdRaw,
      contactKey,
    });

    /* 1️⃣ CRM + tenant policy (Dev V1: client-side read) */
    const crm = await this.#crmUtils.getCurrentCRM(userId); // 'hubspot'|'salesforce'|'local'|null
    this.__tenantSkipCache ??= new Map();
    let tenantSkip = this.__tenantSkipCache.get(tenantId);
    if (tenantSkip === undefined) {
      const snap = await getDoc(docWithTenant(tenantId, 'AppConfig/general'));
      tenantSkip = !!(snap.exists() && snap.data()?.skipCrmCreates);
      this.__tenantSkipCache.set(tenantId, tenantSkip);
    }
    // Allow QA override via method arg, but tenant flag is the source of truth.
    const effectiveSkip = !!(tenantSkip || skipCrmCreates);
    const mirrorOnly = (crm === 'local' || !crm) && effectiveSkip;

    if (!crm && !mirrorOnly) {
      throw new Error(`Seller ${userId} has no CRM connected`);
    }
    if (crm === 'local' && !effectiveSkip) {
      throw new Error("CRM 'local' requires skipCrmCreates to be enabled");
    }

    /* 2️⃣ playbook + due‑date */
    const pbSnap = await getDoc(
      docWithTenant(tenantId, `Playbooks/${playbookId}`)
    );
    if (!pbSnap.exists()) throw new Error(`Playbook ${playbookId} not found`);

    const step0 = pbSnap.data().steps?.['0'] ?? { type: 'CALL' };
    const delay = Number(step0.delayDays ?? 0);
    const base = startISO ? new Date(`${startISO}T00:00`) : new Date();
    const dueDate = addBusinessDayTimestamp(base, delay);
    const dueDateValue = new Date(dueDate);
    const dueDateKey = Number.isNaN(dueDateValue.valueOf())
      ? ''
      : [
          dueDateValue.getFullYear(),
          String(dueDateValue.getMonth() + 1).padStart(2, '0'),
          String(dueDateValue.getDate()).padStart(2, '0'),
        ].join('-');

    /* 2️⃣b resolve contact staging id */
    let contactStagingId = null;
    const directContactSnap = await getDoc(
      docWithTenant(tenantId, `ContactsStaging/${contactKey}`)
    );
    if (directContactSnap.exists()) {
      contactStagingId = contactKey;
    } else {
      const crmQuery = query(
        colWithTenant(tenantId, 'ContactsStaging'),
        where('crmContactId', '==', contactKey),
        limit(1)
      );
      const crmSnap = await getDocs(crmQuery);
      if (!crmSnap.empty) {
        contactStagingId = crmSnap.docs[0].id;
      } else {
        const contactIdQuery = query(
          colWithTenant(tenantId, 'ContactsStaging'),
          where('contactId', '==', contactKey),
          limit(1)
        );
        const contactIdSnap = await getDocs(contactIdQuery);
        if (!contactIdSnap.empty) {
          contactStagingId = contactIdSnap.docs[0].id;
        }
      }
    }

    const enrolId =
      typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    /* 3️⃣ create first CRM task */
    const enrolCRM = mirrorOnly ? 'local' : crm;
    const taskId = String(
      await this.#createInitialTask({
        enrolId,
        playbookId,
        userId,
        crm: enrolCRM,
        contactId: contactKey,
        dueDate,
        dueDateKey,
        step: step0,
        replyInThread: !!pbSnap.data().replyInThread,
        skipCrmCreates: effectiveSkip,
      })
    );

    /* 4️⃣ persist enrolment */
    await writeWithTenant(
      tenantId,
      docWithTenant(tenantId, `SequenceEnrolments/${enrolId}`),
      {
        playbookId,
        userId,
        contactId: contactKey,
        ...(contactStagingId ? { contactStagingId } : {}),
        crm: enrolCRM,
        status: 'IN_PROGRESS',
        stepIndex: 0,
        currentTaskId: taskId,
        steps: { 0: { crmTaskId: taskId, createdAt: serverTimestamp() } },
        createdAt: serverTimestamp(),
      }
    );

    if (shouldConsumeEligibility) {
      await this.#consumeResetEligibility({
        tenantId,
        contactKey,
        enrolId,
      });
    }

    return 'ok';
  }

  /**
   * Resolve tenant id from auth context.
   * @private
   * @returns {Promise<string>}
   */
  async #resolveTenantId() {
    const me =
      this.#authService.currentUser ?? (await this.#authService.waitForLogin());
    const tenantId = me?.tenantId;
    if (!tenantId) {
      console.error('Missing tenant ID');
      throw new Error('Missing tenant ID');
    }
    return tenantId;
  }

  /**
   * Ensure a contact can be enrolled based on active/staged/prior-run guardrails.
   * @private
   * @param {{tenantId:string, contactIdRaw:string, contactKey:string}} params
   * @returns {Promise<{shouldConsumeEligibility:boolean}>}
   */
  async #assertEnrollmentAllowed({ tenantId, contactIdRaw, contactKey }) {
    const [activeSnap, inProgressSnap] = await Promise.all([
      getDocs(
        query(
          colWithTenant(tenantId, 'SequenceEnrolments'),
          where('status', '==', 'ACTIVE'),
          where('contactId', '==', contactKey),
          limit(1)
        )
      ),
      getDocs(
        query(
          colWithTenant(tenantId, 'SequenceEnrolments'),
          where('status', '==', 'IN_PROGRESS'),
          where('contactId', '==', contactKey),
          limit(1)
        )
      ),
    ]);

    const stagedCandidates = buildContactCandidates([contactIdRaw, contactKey]);
    let stagedExists = false;
    for (let i = 0; i < stagedCandidates.length; i += 10) {
      const chunk = stagedCandidates.slice(i, i + 10);
      if (!chunk.length) continue;
      const stagedSnap = await getDocs(
        query(
          colGroupWithTenant('items'),
          where('tenantId', '==', tenantId),
          where('contactRef', 'in', chunk),
          limit(1)
        )
      );
      if (!stagedSnap.empty) {
        stagedExists = true;
        break;
      }
    }

    if (!activeSnap.empty || !inProgressSnap.empty || stagedExists) {
      const err = new Error('contact_active_or_staged');
      err.code = 'contact_active_or_staged';
      throw err;
    }

    const priorSnap = await getDocs(
      query(
        colWithTenant(tenantId, 'SequenceEnrolments'),
        where('contactId', '==', contactKey),
        limit(1)
      )
    );
    if (priorSnap.empty) {
      return { shouldConsumeEligibility: false };
    }

    const eligibilitySnap = await getDoc(
      docWithTenant(tenantId, `SequenceEligibility/${contactKey}`)
    );
    const eligibility = eligibilitySnap.exists()
      ? eligibilitySnap.data() || {}
      : {};
    if (eligibility.eligibleOnce === true) {
      return { shouldConsumeEligibility: true };
    }

    const err = new Error('needs_manual_reset');
    err.code = 'needs_manual_reset';
    throw err;
  }

  /**
   * Consume a one-time reset eligibility token after enrollment succeeds.
   * @private
   * @param {{tenantId:string, contactKey:string, enrolId:string}} params
   * @returns {Promise<void>}
   */
  async #consumeResetEligibility({ tenantId, contactKey, enrolId }) {
    await writeWithTenant(
      tenantId,
      docWithTenant(tenantId, `SequenceEligibility/${contactKey}`),
      {
        contactKey,
        eligibleOnce: false,
        consumedByEnrolId: enrolId,
        consumedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  /**
   * Create the step‑0 CRM task via Cloud Function manageUserTask.
   * @private
   */
  async #createInitialTask({
    enrolId,
    playbookId,
    userId,
    crm,
    contactId,
    dueDate,
    dueDateKey,
    step,
    replyInThread = false,
    skipCrmCreates,
  }) {
    const mirrorOnly = crm === 'local' || !!skipCrmCreates;

    const tplCandidates = [];
    const pushTpl = (bucket, value) => {
      if (Array.isArray(value)) {
        value.forEach((entry) => pushTpl(bucket, entry));
        return;
      }
      if (value === undefined || value === null) return;
      const str = String(value).trim();
      if (str && !bucket.includes(str)) bucket.push(str);
    };

    pushTpl(tplCandidates, step.templates);
    pushTpl(tplCandidates, step.templateIds);
    pushTpl(tplCandidates, step.tpl);
    pushTpl(tplCandidates, step.templateId);
    pushTpl(tplCandidates, step.template);

    const goEmailCandidates = [];
    pushTpl(goEmailCandidates, step.goEmailTemplates);
    pushTpl(goEmailCandidates, step.goEmailTpl);

    const primaryTpl = tplCandidates[0] ?? null;
    const primaryGoEmailTpl = goEmailCandidates[0] ?? null;
    const meta = {
      idx: 0,
      type: step.type,
      tpl: primaryTpl,
      templates: tplCandidates.length ? tplCandidates : null,
      goEmailTpl: primaryGoEmailTpl,
      goEmailTemplates: goEmailCandidates.length ? goEmailCandidates : null,
      callScripts: normaliseCallScripts(step.callScripts),
      data: step.templateData ?? null, // NEW field
      prev: null,
      prevName: null,
      thread: replyInThread,
      name: step.displayName ?? null,
    };

    const subjectLine = `[${step.type}] ${
      step.displayName || primaryTpl || `${playbookId} – Step 1`
    }`;

    if (!mirrorOnly) {
      const body = {
        userId,
        crm,
        action: 'create',
        subject: subjectLine,
        status: 'NOT_STARTED',
        dueDate,
        description: makeMetaComment(meta), // now includes data
        associatedContactId: contactId,
      };
      if (skipCrmCreates) body.skipCrmCreates = skipCrmCreates;

      const { taskId } = await this.#apiClient.fetchJson(
        this.#endpoints.manageUserTask(),
        {
          auth: true,
          body,
        }
      );

      if (!taskId) throw new Error('manageUserTask returned no taskId');
      return taskId;
    }

    const me =
      this.#authService.currentUser ?? (await this.#authService.waitForLogin());
    const tenantId = me?.tenantId;
    if (!tenantId) {
      console.error('Missing tenant ID');
      throw new Error('Missing tenant ID');
    }
    const taskId =
      typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    await writeWithTenant(
      tenantId,
      docWithTenant(tenantId, `mirrorTasks/${taskId}`),
      {
        tenantId,
        taskId,
        assignedToUid: userId,
        status: 'OPEN',
        dueAt: new Date(dueDate),
        ...(dueDateKey ? { dueDate: dueDateKey } : {}),
        title: subjectLine,
        contactId,
        enrolId,
        sequenceId: enrolId,
        playbookId,
        stepIndex: 0,
        stepIdx: 0,
        metadata: { notes: makeMetaComment(meta) },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
    );
    return taskId;
  }
}

/**
 * Convenience helper for DI setups that expose an already‑constructed service.
 * @param {SequenceEnrollmentService} svc – instantiated service
 * @param {string} playbookId – playbook to enrol in
 * @param {{userId:string,contactId:string}[]} tuples – enrolment tuples
 * @param {string|null} [startISO=null] – optional start date ISO string
 * @param {boolean} [skipCrmCreates] – opt-out of CRM creates (QA only)
 * @returns {Promise<{successIds:string[],skipIds:string[],failIds:string[]}>}
 */
export const enrolContacts = (
  svc,
  playbookId,
  tuples,
  startISO = null,
  skipCrmCreates
) => svc.enrolContacts(playbookId, tuples, startISO, skipCrmCreates);
